import { parse as parseDateFns } from "date-fns";

export type BulkParseRow = {
  id: string;
  address: string;
  checkoutAt: Date | null;
  checkinAt: Date | null;
  windowStartAt: Date | null;
  windowEndAt: Date | null;
  guests: number | null;
  hasPets: boolean | null;
  hasKids: boolean | null;
  notes: string | null;
  warnings: string[];
  errors: string[];
  confidence: number;
};

export type BulkParseResult = {
  rows: BulkParseRow[];
  truncated: boolean;
};

export type BulkParseOptions = {
  defaultCheckoutTime: string; // "11:00"
  defaultCheckinTime: string; // "15:00"
  bufferMinutes: number; // e.g. 60
  maxRows: number; // e.g. 30
};

const DATE_FORMATS = [
  "dd/MM/yyyy HH:mm",
  "dd/MM/yyyy H:mm",
  "dd/MM/yyyy",
  "dd-MM-yyyy HH:mm",
  "dd-MM-yyyy H:mm",
  "dd-MM-yyyy",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd H:mm",
  "yyyy-MM-dd",
];

function uuidLike() {
  // lightweight unique id (no crypto dependency)
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isValidDate(d: any): d is Date {
  return d instanceof Date && !isNaN(d.getTime());
}

function parseDateLoose(raw: string): Date | null {
  const s = String(raw || "").trim();
  if (!s) return null;
  // ISO / native
  const native = new Date(s);
  if (isValidDate(native)) return native;
  // date-fns formats (assume local)
  for (const fmt of DATE_FORMATS) {
    const d = parseDateFns(s, fmt, new Date());
    if (isValidDate(d)) return d;
  }
  return null;
}

function normalizeTime(t: string): string | null {
  const s = String(t || "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Math.max(0, Math.min(23, Number(m[1])));
  const mm = Math.max(0, Math.min(59, Number(m[2])));
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function withDefaultTime(dateOnly: Date, timeHHmm: string): Date {
  const [hh, mm] = timeHHmm.split(":").map(Number);
  const d = new Date(dateOnly);
  d.setHours(hh || 0, mm || 0, 0, 0);
  return d;
}

function detectDelimiter(line: string): string | null {
  if (line.includes("\t")) return "\t";
  const candidates = [",", ";", "|"];
  for (const c of candidates) {
    const parts = line.split(c);
    if (parts.length >= 3) return c;
  }
  // multiple spaces
  if (/\s{2,}/.test(line)) return "__spaces__";
  return null;
}

function splitByDelimiter(line: string, delim: string | null): string[] {
  if (!delim) return [line];
  if (delim === "__spaces__") return line.split(/\s{2,}/g);
  return line.split(delim);
}

function extractGuests(raw: string): number | null {
  const s = String(raw || "").toLowerCase();
  const m = s.match(/(\d{1,3})\s*(pax|ospiti|guests|persone)\b/);
  if (m) return Number(m[1]);
  // fallback: a plain number token if small
  const n = s.match(/\b(\d{1,2})\b/);
  if (n) {
    const v = Number(n[1]);
    if (v >= 1 && v <= 20) return v;
  }
  return null;
}

function extractFlags(raw: string): { hasPets: boolean | null; hasKids: boolean | null } {
  const s = String(raw || "").toLowerCase();
  const hasPets = /\b(cane|cani|dog|pet|animale|animali)\b/.test(s) ? true : null;
  const hasKids = /\b(bambin|kids|child|children|culla)\b/.test(s) ? true : null;
  return { hasPets, hasKids };
}

function findTwoDatesFreeform(line: string): { d1: Date | null; d2: Date | null } {
  // Extract up to 2 date-like tokens. We keep it conservative.
  const tokens = line.match(/\b(\d{2}[\/-]\d{2}[\/-]\d{4}(?:\s+\d{1,2}:\d{2})?)\b|\b(\d{4}-\d{2}-\d{2}(?:\s+\d{1,2}:\d{2})?)\b/g);
  const d1 = tokens?.[0] ? parseDateLoose(tokens[0]) : null;
  const d2 = tokens?.[1] ? parseDateLoose(tokens[1]) : null;
  return { d1, d2 };
}

export function parseBulkPaste(rawText: string, opts: BulkParseOptions): BulkParseResult {
  const lines = String(rawText || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const out: BulkParseRow[] = [];
  let truncated = false;

  for (const line of lines) {
    if (out.length >= opts.maxRows) {
      truncated = true;
      break;
    }
    const warnings: string[] = [];
    const errors: string[] = [];

    const delim = detectDelimiter(line);
    const cols = splitByDelimiter(line, delim).map((c) => c.trim()).filter((c) => c.length > 0);

    // Heuristic mapping (no explicit header support in v1)
    // Try: [address, checkout, checkin, guests/notes]
    let address = cols[0] || line;
    let checkoutRaw = cols[1] || "";
    let checkinRaw = cols[2] || "";
    let tail = cols.slice(3).join(" ");

    // If we have only 1 column, attempt freeform extraction
    if (cols.length < 3) {
      const { d1, d2 } = findTwoDatesFreeform(line);
      checkoutRaw = d1 ? line.match(/(\d{2}[\/-]\d{2}[\/-]\d{4}(?:\s+\d{1,2}:\d{2})?)|(\d{4}-\d{2}-\d{2}(?:\s+\d{1,2}:\d{2})?)/)?.[0] || "" : "";
      checkinRaw = d2 ? line.match(/(\d{2}[\/-]\d{2}[\/-]\d{4}(?:\s+\d{1,2}:\d{2})?)|(\d{4}-\d{2}-\d{2}(?:\s+\d{1,2}:\d{2})?)/g)?.[1] || "" : "";
      // Try to keep address as the part before first date token
      const firstDateToken = checkoutRaw;
      if (firstDateToken && line.includes(firstDateToken)) {
        address = line.split(firstDateToken)[0].trim().replace(/[,\-|]+$/g, "").trim() || address;
        tail = line.split(firstDateToken).slice(1).join(" ").trim();
      }
    }

    const checkoutParsed = parseDateLoose(checkoutRaw);
    const checkinParsed = parseDateLoose(checkinRaw);

    let checkoutAt: Date | null = checkoutParsed;
    let checkinAt: Date | null = checkinParsed;

    const checkoutDefault = normalizeTime(opts.defaultCheckoutTime) || "11:00";
    const checkinDefault = normalizeTime(opts.defaultCheckinTime) || "15:00";

    // If parsed date has 00:00 and input looked date-only, apply default time
    if (checkoutAt && /^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(checkoutRaw.trim()) || (checkoutAt && /^\d{4}-\d{2}-\d{2}$/.test(checkoutRaw.trim()))) {
      checkoutAt = withDefaultTime(checkoutAt, checkoutDefault);
      warnings.push("Checkout senza orario: applicata ora default");
    }
    if (checkinAt && /^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(checkinRaw.trim()) || (checkinAt && /^\d{4}-\d{2}-\d{2}$/.test(checkinRaw.trim()))) {
      checkinAt = withDefaultTime(checkinAt, checkinDefault);
      warnings.push("Check-in senza orario: applicata ora default");
    }

    if (!address || address.length < 6) errors.push("Indirizzo mancante o troppo corto");
    if (!checkoutAt) errors.push("Checkout non valido");
    if (!checkinAt) errors.push("Check-in non valido");

    let windowStartAt: Date | null = null;
    let windowEndAt: Date | null = null;
    if (checkoutAt && checkinAt) {
      windowStartAt = checkoutAt;
      windowEndAt = new Date(checkinAt.getTime() - Math.max(0, opts.bufferMinutes) * 60_000);
      if (windowEndAt.getTime() <= windowStartAt.getTime()) {
        errors.push("Finestra non valida (check-in troppo vicino al checkout)");
      }
    }

    const guests = extractGuests([cols[3], tail].filter(Boolean).join(" "));
    const flags = extractFlags([cols.join(" "), tail].join(" "));

    // Notes = leftover text beyond address+dates if present
    const notes = (tail || "").trim() ? (tail || "").trim() : null;

    // Confidence: base 1, subtract for issues
    let confidence = 1;
    confidence -= Math.min(0.5, warnings.length * 0.1);
    confidence -= Math.min(0.7, errors.length * 0.2);
    confidence = Math.max(0, Math.min(1, confidence));

    out.push({
      id: uuidLike(),
      address,
      checkoutAt,
      checkinAt,
      windowStartAt,
      windowEndAt,
      guests,
      hasPets: flags.hasPets,
      hasKids: flags.hasKids,
      notes,
      warnings,
      errors,
      confidence,
    });
  }

  return { rows: out, truncated };
}

